import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, AlertTitle, Button } from '@mui/material';
import ImageViewer from '@/components/viewers/ImageViewer';
import SimpleLayout from '@/components/SimpleLayout';

/**
 * Type definition for error state
 * @typedef {Object} ImageViewerError
 * @property {string} message - Error message to display
 */
type ImageViewerError = {
  message: string;
};

/**
 * Image Viewer Page Component
 * 
 * A full-page image viewer that displays an image in a modal-like interface.
 * Retrieves image data from session storage and handles error states.
 * 
 * @component
 * @returns {JSX.Element} The image viewer page component
 * 
 * @example
 * // To use this component, set the image data in session storage:
 * sessionStorage.setItem('imageViewer', JSON.stringify({
 *   url: 'https://example.com/image.jpg',
 *   name: 'Example Image'
 * }));
 * 
 * @description
 * This component provides:
 * - Full-screen image viewing experience
 * - Loading state while image is being loaded
 * - Error handling for missing or invalid image data
 * - Navigation back to the previous page
 */
const ImageViewerPage: React.FC = () => {
  /**
   * State for managing image data and UI state
   * @type {Object}
   * @property {string} imageUrl - URL of the image to display
   * @property {string} fileName - Name of the image file
   * @property {ImageViewerError | null} error - Error state for handling display errors
   */
  const [imageUrl, setImageUrl] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [error, setError] = useState<ImageViewerError | null>(null);
  const navigate = useNavigate();

  /**
   * Effect hook to load image data from session storage on component mount
   * @effect
   * @dependencies []
   * @description
   * - Retrieves image data from session storage
   * - Validates the data and updates component state
   * - Sets error state if data is missing or invalid
   */
  useEffect(() => {
    // Get image info from session storage
    const imageInfo = sessionStorage.getItem('imageViewer');
    
    if (!imageInfo) {
      setError({ message: 'No image information found. Please go back and try again.' });
      return;
    }

    try {
      const { url, name } = JSON.parse(imageInfo);
      if (!url) {
        throw new Error('Invalid image URL');
      }
      setImageUrl(url);
      setFileName(name || 'Image');
    } catch (err) {
      console.error('Error parsing image info:', err);
      setError({ 
        message: 'Invalid image information. Please go back and try again.' 
      });
    }
  }, []);

  /**
   * Handles the close action for the image viewer
   * @function handleClose
   * @returns {void}
   * @description
   * - Clears the image data from session storage
   * - Navigates back to the previous page
   */
  const handleClose = (): void => {
    // Clear the session storage and navigate back
    sessionStorage.removeItem('imageViewer');
    navigate(-1); // Go back to previous page
  };

  // Render error state if there's an error
  if (error) {
    return (
      <SimpleLayout>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Error</AlertTitle>
            {error.message}
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => navigate(-1)}
          >
            Go Back
          </Button>
        </Box>
      </SimpleLayout>
    );
  }

  // Show loading state while image URL is being loaded
  if (!imageUrl) {
    return (
      <SimpleLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </SimpleLayout>
    );
  }

  // Render the ImageViewer component with the loaded image
  return (
    <SimpleLayout>
      <ImageViewer 
        imageUrl={imageUrl} 
        fileName={fileName} 
        onClose={handleClose} 
      />
    </SimpleLayout>
  );
};

export default ImageViewerPage;
